import { cloneDeep, get, isEmpty, noop, parseInt } from "lodash-es";
import {
  AMOUNT_RANGE,
  DEFAULT_RISK_TYPE,
  INVEST_TYPE_AMOUNTS,
  INVEST_TYPE_DEFAULT_VALUES,
  MAXIMUM_SAVETAX_AMOUNT,
  MINIMUM_SIP_AMOUNT,
  ONE_TIME_FLOWS,
  SIP_FLOWS,
} from "../../constants/mf";
import {
  CUSTOM_AMOUNT_ADD_OBJ,
  CUSTOM_GOAL_TARGET_MAPPER,
  GOAL_IMAGE_MAPPER,
  SAVE_GOAL_MAPPER,
  SAVE_GOAL_NAMES,
} from "../../constants/mf/investGoalSelectAmount";
import {
  CHANGE_RISK_PROFILE,
  INVESTMENT_TYPE,
  RECOMMENDED_FUNDS,
} from "../../strings/mf";
import { numDifferentiationWithNoSpace } from "../common/functions";
import { EVENT_SCREEN_NAMES } from "../../events/mf";
import {
  isCustomRiskFlow,
  isSaveTaxFlow,
  isSaveTaxSipFlow,
  validateAmount,
  isSipFlow,
  isParkMoneyFlow,
  isSaveForGoalFlow,
  isRiskProfileEnabledFlow,
} from "./validators";
import { capitalizeName } from "../common/functions";
import { format, parse } from "date-fns";

const date = new Date();
const month = date.getMonth();
const currentMonth = month + 1;
let currentYear = date.getFullYear();

export const getSaveTaxYear = () => {
  let year = currentYear;
  if (currentMonth > 3) {
    year = year + 1;
  }
  return year;
};

export const getChangeRiskProfileViewData = (mfRecommendations = {}) => {
  let riskOptions = CHANGE_RISK_PROFILE.RISK_OPTIONS || [];
  const customOptionData = CHANGE_RISK_PROFILE.CUSTOM_OPTION_DATA;
  let selectedRisk = mfRecommendations.rp_indicator || "";
  let headerTitle = CHANGE_RISK_PROFILE.navigationHeader.title;
  const { equity, debt } = mfRecommendations;
  let isModifyFlow = false;
  const customRiskFlow = isCustomRiskFlow(mfRecommendations);
  let eventScreenName = EVENT_SCREEN_NAMES.SELECT_RISK_PROFILE;
  if (!isEmpty(selectedRisk)) {
    isModifyFlow = true;
    eventScreenName = EVENT_SCREEN_NAMES.CHANGE_RISK_PROFILE;
    if (customRiskFlow) {
      selectedRisk = customOptionData.value;
      riskOptions = [
        ...riskOptions,
        {
          ...customOptionData,
          subtitle: customOptionData.subtitle(equity, debt),
        },
      ];
    }
  } else {
    selectedRisk = DEFAULT_RISK_TYPE;
    headerTitle = CHANGE_RISK_PROFILE.navigationHeader.defaultTitle;
  }
  return {
    riskOptions,
    selectedRisk,
    isModifyFlow,
    headerTitle,
    customRiskFlow,
    eventScreenName,
  };
};

export const getSelectedRiskViewData = (mfRecommendations = {}) => {
  const {
    riskOptions = {},
    selectedRisk = "",
    customRiskFlow = false,
  } = getChangeRiskProfileViewData(mfRecommendations);
  const riskOptionData =
    riskOptions.find((data) => data.value === selectedRisk) || {};
  const riskData = CHANGE_RISK_PROFILE.riskData;
  const subtext = customRiskFlow
    ? riskData.customSubtitle
    : riskData.defaultSubtitle;
  let selectedRiskOptionData = cloneDeep(riskOptionData);
  selectedRiskOptionData.title = customRiskFlow
    ? `${riskOptionData.title} ${riskData.titleSubtext}`
    : `${riskOptionData.title}`;
  selectedRiskOptionData.subtext = subtext;
  return selectedRiskOptionData;
};

export const getOrderType = (investType = '') => {
  const sipFlow = isSipFlow(investType);
  let orderType = ONE_TIME_FLOWS.onetime;
  if (sipFlow) {
    orderType = SIP_FLOWS.sip;
  }
  return orderType;
}

export const getCorpusValue = (
  stockSplitVal,
  amount,
  isRecurring,
  term,
  stockReturns,
  bondReturns
) => {
  let principle = Number(amount);
  let corpusValue = 0;
  for (let i = 0; i < term; i++) {
    if (isRecurring) {
      const months = (i + 1) * 12;
      let rateOfIntrest =
        getRateOfInterest(stockSplitVal, stockReturns, bondReturns) / 12 / 100;
      corpusValue =
        (amount * (Math.pow(1 + rateOfIntrest, months) - 1)) / rateOfIntrest;
    } else {
      const currInterest =
        (principle *
          getRateOfInterest(stockSplitVal, stockReturns, bondReturns)) /
        100;
      corpusValue = principle + currInterest;
      principle += currInterest;
    }
  }
  return corpusValue;
};

export const getRateOfInterest = (stockSplitVal, stockReturns, bondReturns) => {
  const range = Math.abs(stockReturns - bondReturns);
  if (stockSplitVal < 1) {
    return bondReturns;
  } else if (stockSplitVal > 99) {
    return stockReturns;
  } else {
    const rateOffset = (range * stockSplitVal) / 100;
    return bondReturns + rateOffset;
  }
};

export const calculateCorpus = ({
  amount,
  investType,
  equityRatio,
  isRecurring,
  flowName,
  term,
  stockReturns,
  bondReturns,
}) => {
  if (isSaveTaxFlow(flowName)) {
    return calculateTax(amount, investType);
  }
  return getCorpusValue(
    equityRatio,
    amount,
    isRecurring,
    term,
    stockReturns,
    bondReturns
  );
};

export const calculateTax = (amount, investType) => {
  const MARCH_MONTH_NUMBER = 3;
  const MAXIMUM_TAX_SAVABLE_MONTHS = 15;
  const TAX_SAVING_PERCENTAGE = 0.312;
  let duration =
    currentMonth > MARCH_MONTH_NUMBER
      ? MAXIMUM_TAX_SAVABLE_MONTHS - currentMonth
      : MARCH_MONTH_NUMBER - currentMonth;
  if (duration === 0) {
    duration = 1;
  }
  let tempAmount = 0;
  const saveTaxSipFlow = isSaveTaxSipFlow(investType);
  if (saveTaxSipFlow) {
    tempAmount = amount * duration;
  } else {
    tempAmount = amount;
  }
  if (tempAmount > MAXIMUM_SAVETAX_AMOUNT) {
    tempAmount = MAXIMUM_SAVETAX_AMOUNT;
  }
  const taxAmount = tempAmount * TAX_SAVING_PERCENTAGE;
  return taxAmount;
};

export const getGoalMinMaxAmount = (goal = {}, investType) => {
  let result = {};
  if (isSipFlow(investType)) {
    result.minValue = goal.min_sip_amount || AMOUNT_RANGE.sip.min;
    result.maxValue = goal.max_sip_amount || AMOUNT_RANGE.sip.max;
  } else {
    result.minValue = goal.min_ot_amount || AMOUNT_RANGE.onetime.min;
    result.maxValue = goal.max_ot_amount || AMOUNT_RANGE.onetime.max;
  }

  return result;
};

export const getInvestedValue = (term, amount, isRecurring) => {
  return isRecurring ? amount * 12 * term : amount;
};

export const getInvestmentTypeViewData = (flow, mfRecommendations = {}) => {
  const {
    equity,
    stockReturns,
    bondReturns,
    term,
    amountData = {},
    investOption = "",
  } = mfRecommendations;
  const isInFlow = flow === mfRecommendations.flowType;
  const screenContent = INVESTMENT_TYPE[flow];
  let defaultInvestType = INVEST_TYPE_DEFAULT_VALUES[flow];
  let defaultInvestmentAmountData = INVEST_TYPE_AMOUNTS[flow] || {};
  const isRpEnabled = isRiskProfileEnabledFlow(flow);

  if (isInFlow) {
    if (!isEmpty(investOption)) {
      defaultInvestType = investOption;
    }
    if (!isEmpty(amountData)) {
      defaultInvestmentAmountData = amountData;
    }
  }
  let defaultAmountValidationData = {};
  let defaultCorpusData = {};
  if (isSaveForGoalFlow(flow)) {
    defaultInvestmentAmountData = {
      [defaultInvestType]: getMonthlyCommitmentAmount(
        term,
        mfRecommendations.corpus,
        equity,
        stockReturns,
        bondReturns
      ),
    };
  }
  if (!isEmpty(defaultInvestmentAmountData)) {
    Object.keys(defaultInvestmentAmountData).forEach((type) => {
      const amountValue = defaultInvestmentAmountData[type];
      const amountValidation = validateAmount(
        amountValue,
        mfRecommendations,
        type
      );
      const isRecurring = isSipFlow(type);
      let termValue = term;
      if (isParkMoneyFlow(flow)) {
        const options = get(screenContent, "investmentCard.options", []);
        const selectedOption = options.find((data) => data.value === type);
        termValue = selectedOption.term;
      }
      const corpusValue = calculateCorpus({
        amount: amountValue,
        investType: type,
        stockReturns,
        bondReturns,
        term: termValue,
        equityRatio: equity,
        isRecurring,
        flowName: flow,
      });

      defaultAmountValidationData[type] = amountValidation;
      defaultCorpusData[type] = corpusValue;
    });
  }
  return {
    screenContent,
    defaultInvestType,
    defaultInvestmentAmountData,
    defaultAmountValidationData,
    defaultCorpusData,
    isRpEnabled,
  };
};

export const resetDefaultMfRecommendationsData = (recommendations = {}) => {
  let data = cloneDeep(recommendations);
  data.amountData = {};
  data.investOption = "";
  return data;
};

export const getMonthlyCommitmentAmount = (
  term,
  corpusValue,
  stockSplitVal,
  stockReturns,
  bondReturns
) => {
  const months = term * 12;
  const rateOfIntrest = getRateOfInterest(
    stockSplitVal,
    stockReturns,
    bondReturns
  );
  const roiPercentage = rateOfIntrest / 12 / 100;
  const tmp = Math.pow(1 + roiPercentage, months) - 1;
  let monthlyInvestment = (corpusValue * roiPercentage) / tmp;

  if (monthlyInvestment < MINIMUM_SIP_AMOUNT) {
    monthlyInvestment = MINIMUM_SIP_AMOUNT;
  }
  return Math.floor(monthlyInvestment);
};

export const getRecommendedFundsViewData = (mfRecommendations = {}) => {
  const totalAmount = mfRecommendations.amount;
  const fundList = mfRecommendations.recommendation || [];
  const riskData = getSelectedRiskViewData(mfRecommendations);
  const flowType = mfRecommendations.flowType;
  const hideRiskData = !isRiskProfileEnabledFlow(flowType);
  const isSip = isSipFlow(mfRecommendations.investType);
  const totalAmountData = RECOMMENDED_FUNDS.totalAmount.title;
  const totalAmountTitle = isSip
    ? totalAmountData.sip
    : totalAmountData.onetime;
  return {
    totalAmount,
    fundList,
    riskData,
    hideRiskData,
    totalAmountTitle,
  };
};

export const getAlternateFunds = (fundObj = {}, mfRecommendations = {}) => {
  const alternateFunds = filterReplaceFunds(
    fundObj.mf.mftype,
    mfRecommendations
  );
  if (!isEmpty(alternateFunds)) {
    return { originalFund: fundObj, alternateFunds };
  }
  return {
    error: true,
  };
};

const filterReplaceFunds = (mftype, mfRecommendations = {}) => {
  const { recommendation = [], alternatives = {} } = mfRecommendations;
  const funds = get(alternatives, [mftype], []);
  let alternateFunds = cloneDeep(funds);
  recommendation.forEach((el) => {
    alternateFunds.forEach((alt, idx) => {
      if (alt.mf.mfid === el.mf.mfid) {
        alternateFunds.splice(idx, 1);
      }
    });
  });
  return alternateFunds;
};

export const onSelectionOfReplaceFund = (
  selectedFundMfidToReplace = "",
  alternateFunds = [],
  originalFund = {},
  setSelectedFundCallback = noop,
  callbackBottomsheet = noop,
  mfRecommendations = {}
) => {
  let remainingAmount = 0;
  const replaceableFund = alternateFunds.find(
    (fund) => fund.mf.mfid === selectedFundMfidToReplace
  );
  setSelectedFundCallback(replaceableFund);
  if (replaceableFund) {
    const newFundAmountMultiple = replaceableFund.mf.mfamountmultiple;
    const isAmountMultipleError =
      originalFund.mf.mfamountmultiple < newFundAmountMultiple &&
      originalFund.amount % newFundAmountMultiple !== 0;
    const isMinPurchaseError =
      originalFund.amount < replaceableFund.mf.min_purchase;
    if (isAmountMultipleError || isMinPurchaseError) {
      // show min amount bottomsheet here
      callbackBottomsheet();
      const mod = originalFund.amount % newFundAmountMultiple;
      let updatedAmount = originalFund.amount - mod;
      if (updatedAmount < replaceableFund.mf.min_purchase) {
        updatedAmount = replaceableFund.mf.min_purchase;
      }
      return {
        error: true,
        updatedAmount,
      };
    } else {
      return getRecommendationFunds(
        { ...replaceableFund },
        originalFund,
        mfRecommendations,
        remainingAmount
      );
    }
  } else {
    return {
      error: true,
    };
    // here hide bottomsheet if needed
  }
};

export const getRecommendationFunds = (
  replaceableFunds,
  originalFund,
  mfRecommendations,
  remainingAmount
) => {
  let totalAmount = 0;
  const newRecommendedFunds = replaceFundsAmount(
    { ...replaceableFunds },
    originalFund,
    mfRecommendations,
    remainingAmount
  );
  const finalRecommendedFunds = newRecommendedFunds.map((fund) => {
    totalAmount += fund.amount;
    if (fund.mf.mfid === originalFund.mf.mfid) {
      return replaceableFunds;
    }
    return fund;
  });
  return { recommendation: finalRecommendedFunds, amount: totalAmount };
};

const replaceFundsAmount = (
  newFund,
  originalFund,
  mfRecommendations,
  remainingAmount
) => {
  const fundCount = mfRecommendations.recommendation.length;
  const originalFundAmountMultiple = originalFund.mf.mfamountmultiple;
  const newFundAmountMultiple = newFund.mf.mfamountmultiple;

  if (
    originalFundAmountMultiple === newFundAmountMultiple ||
    newFundAmountMultiple === 1
  ) {
    newFund.amount = originalFund.amount;
    return updateRecommendationFunds(
      newFund,
      mfRecommendations.recommendation,
      originalFund
    );
  }

  if (newFundAmountMultiple > originalFund.amount) {
    newFund.amount = newFundAmountMultiple;
    return updateRecommendationFunds(
      newFund,
      mfRecommendations.recommendation,
      originalFund
    );
  }

  if (newFundAmountMultiple !== originalFundAmountMultiple) {
    const mod = originalFund.amount % newFundAmountMultiple;
    if (mod === 0) {
      newFund.amount = originalFund.amount;
      return updateRecommendationFunds(
        newFund,
        mfRecommendations.recommendation,
        originalFund
      );
    } else {
      newFund.amount = originalFund.amount - mod;
      const updatedRecommendationFunds = updateRecommendationFunds(
        newFund,
        mfRecommendations.recommendation,
        originalFund
      );
      if (mod > 0) {
        remainingAmount = Math.floor(mod % fundCount);
        const amountLeftToDistribute = mod / fundCount;
        return addRemainingAmountToFunds(
          amountLeftToDistribute,
          updatedRecommendationFunds,
          remainingAmount
        );
      }
      return updatedRecommendationFunds;
    }
  }
};

const updateRecommendationFunds = (
  newFund = {},
  recommendation = [],
  originalFund = {}
) => {
  let fundData = cloneDeep(newFund);
  if (fundData.amount < fundData.mf.min_purchase) {
    fundData.amount = fundData.mf.min_purchase;
  }
  const updatedRecommendationFunds = recommendation.map((el, idx) => {
    if (el.mf.mfid === originalFund.mf.mfid) {
      return fundData;
    }
    return el;
  });
  return updatedRecommendationFunds;
};

const addRemainingAmountToFunds = (
  amountLeftToDistribute,
  updatedRecommendationFunds,
  remainingAmount
) => {
  const newRecommendedFunds = updatedRecommendationFunds.map((data) => {
    let fund = cloneDeep(data);
    const currentFundMultiple = fund.mf.mfamountmultiple;
    const mod = Math.floor(amountLeftToDistribute % currentFundMultiple);
    const amountToBeAdded = Math.floor(amountLeftToDistribute - mod);
    fund.amount = fund.amount + amountToBeAdded;
    remainingAmount = remainingAmount + mod;
    return fund;
  });
  if (remainingAmount > 0) {
    return setFinalAmountForReplaceFund(newRecommendedFunds, remainingAmount);
  }
  return newRecommendedFunds;
};

const setFinalAmountForReplaceFund = (newRecommendedFunds, remainingAmount) => {
  if (remainingAmount >= 1000) {
    updateAmountForReplaceFund(newRecommendedFunds, 1000, remainingAmount);
  } else if (remainingAmount >= 500 && remainingAmount < 1000) {
    updateAmountForReplaceFund(newRecommendedFunds, 500, remainingAmount);
  } else if (remainingAmount >= 100 && remainingAmount < 500) {
    updateAmountForReplaceFund(newRecommendedFunds, 100, remainingAmount);
  }
  if (remainingAmount > 0) {
    newRecommendedFunds.some((fund) => {
      if (fund.mf.mfamountmultiple === 1) {
        fund.amount = fund.amount + remainingAmount;
        remainingAmount = 0;
        return true;
      }
    });
  }
  return newRecommendedFunds;
};

const updateAmountForReplaceFund = (newFund, amountMod, remainingAmount) => {
  newFund.some((fund) => {
    if (fund.mf.mfamountmultiple === amountMod) {
      fund.amount =
        fund.amount + remainingAmount - (remainingAmount % amountMod);
      remainingAmount = remainingAmount % amountMod;
      if (remainingAmount > 0) {
        setFinalAmountForReplaceFund(newFund);
      }
      return true;
    }
  });
};

export const getSelectSipDateViewData = (allocations = []) => {
  const sipData = allocations.map((data, index) => {
    return {
      index: index,
      id: data.id || "",
      amount: data.amount,
      logo: data.amc_logo_small,
      date: data.default_date,
      fundName: data.mfname,
      sipDates: data.sip_dates,
      selectedIndex: data.sip_dates.indexOf(data.default_date),
    };
  });
  return sipData;
};

export const getTargetAmountCorpusValue = (amount, mfRecommendations = {}) => {
  const { term, equity, stockReturns, bondReturns } = mfRecommendations;
  const months = term * 12;
  const rateOfIntrest = getRateOfInterest(equity, stockReturns, bondReturns);
  const roiPercentage = rateOfIntrest / 12 / 100;
  const tmp = Math.pow(1 + roiPercentage, months) - 1;
  let corpus = (amount * tmp) / roiPercentage;
  corpus = parseInt(corpus);
  return corpus;
};

export const getTargetAmountViewData = (mfRecommendations = {}) => {
  let defaultAmount = CUSTOM_GOAL_TARGET_MAPPER[mfRecommendations.subtype];
  let defaultErrorData = validateAmount(
    defaultAmount,
    mfRecommendations,
    mfRecommendations.flowType,
    true
  );
  if (defaultErrorData.error) {
    defaultAmount = defaultErrorData.formatedMaxValue;
    defaultErrorData.error = false;
  }
  return { defaultAmount, defaultErrorData };
};

export const getTargetYears = () => {
  const years = Array.from(
    { length: 100 },
    (_, value) => currentYear + value + 1
  );
  return years;
};

export const calculateSaveForGoalCorpusValue = (
  amount = "",
  mfRecommendations = {}
) => {
  // eslint-disable-next-line radix
  return Math.round(
    amount * Math.pow(1 + 0.05, parseInt(mfRecommendations.term))
  );
};

export const getInvestForGoalAmountViewData = (mfRecommendations = {}) => {
  const subtype = mfRecommendations.subtype || "";
  const subtypeData = SAVE_GOAL_MAPPER[subtype] || [];
  const amountMapper = [...subtypeData, CUSTOM_AMOUNT_ADD_OBJ];
  const subtypeAmountList = amountMapper.map((data) => {
    const isOthers = data.name === SAVE_GOAL_NAMES.Others;
    const amount = data.corpus;
    let corpusValue = calculateSaveForGoalCorpusValue(
      amount,
      mfRecommendations
    );
    if (isOthers) {
      corpusValue = CUSTOM_GOAL_TARGET_MAPPER[subtype];
    }
    const icon = GOAL_IMAGE_MAPPER[data.name];
    const title = !isOthers
      ? numDifferentiationWithNoSpace(corpusValue, true, 2)
      : data.label;
    const subtitle = !isOthers ? data.label : "";
    return {
      ...data,
      corpusValue,
      isOthers,
      icon,
      title,
      subtitle,
    };
  });
  return {
    subtypeAmountList,
    subtypeName: mfRecommendations.subtypeName,
  };
};

export const formatLabelWord = (text) => {
  return capitalizeName(text).replace("_", " ");
};

export const getFundPeriod = (startDate, endDate) => {
  const parsedDate1 = parse(startDate, "dd/MM/yyyy", new Date());
  const parsedDate2 = parse(endDate, "dd/MM/yyyy", new Date());
  const start1 = format(parsedDate1, "MMM dd");
  const end = format(parsedDate2, "dd MMM yy");
  const start2 = format(parsedDate1, "dd MMM");

  const listLabelDate = `From ${start1} - ${end}`;
  const validityPeriod = `${start2} - ${end}`;
  return {
    listLabelDate,
    validityPeriod,
  };
};

export const getNfoFundDetailsViewData = (nfoFundDetails = {}) => {
  const startDate = nfoFundDetails.start_date;
  const endDate = nfoFundDetails.end_date;
  const { validityPeriod } = getFundPeriod(startDate, endDate);

  return {
    logo: nfoFundDetails.amc_logo_small,
    title: nfoFundDetails.friendly_name,
    risk: nfoFundDetails.riskometer_v2,
    taxPlan: nfoFundDetails.tax_plan,
    overview: nfoFundDetails.overview,
    exitLoad: nfoFundDetails.exit_load,
    redemptionDisclosure: nfoFundDetails.redemption_disclosure,
    sidLink: nfoFundDetails.sid_link,
    allotmentDate: nfoFundDetails.allotment_date,
    lockinIn: nfoFundDetails.lockin_in,
    validityPeriod: validityPeriod,
  };
};

export const getNfoFundsViewData = (fund = {}) => {
  const startDate = fund.start_date;
  const endDate = fund.end_date;
  const { listLabelDate } = getFundPeriod(startDate, endDate);
  return {
    listLabelDate: listLabelDate,
    iconSrc: fund.amc_logo_small,
    title: fund.friendly_name,
    label1: capitalizeName(fund.tax_plan),
    label2: formatLabelWord(fund.scheme_option),
  };
};
